<template>
  <div class="caregiver-archive" ref="pageRoot">
    <div class="page-header">
      <h2>护工档案</h2>
      <div class="header-actions">
        <a-input
          v-model="keyword"
          allowClear
          class="search-input"
          placeholder="搜索工号/姓名/手机号"
          @pressEnter="handleSearch"
        />
        <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
        <a-button type="primary" icon="plus" @click="openCreate">新增护工</a-button>
      </div>
    </div>

    <div class="archive-body" ref="archiveBody" :style="bodyStyle">
      <a-spin :spinning="loading">
        <div v-if="caregivers.length" class="caregiver-grid">
          <div v-for="cg in caregivers" :key="cg.id" class="cg-card">
            <div class="cg-avatar-wrap">
              <img
                v-if="hasCaregiverAvatar(cg)"
                :src="resolveUrl(cg.avatar)"
                class="cg-photo"
                alt="护工照片"
                @error="markAvatarFailed(cg.id)"
              />
              <img v-else :src="defaultCaregiverAvatar" class="cg-photo cg-photo--default" alt="默认护工头像" />
            </div>
            <div class="cg-info">
              <div class="cg-name-row">
                <span class="cg-name">{{ cg.name }}</span>
                <span class="level-tag" :class="'level-' + cg.levelColor">{{ cg.level }}</span>
              </div>
              <p class="cg-meta">工号：{{ cg.jobNo || "-" }}</p>
              <p class="cg-meta">{{ cg.hours || 0 }} 小时服务经验</p>
              <div class="cg-stats-row">
                <span class="cg-stat"><a-icon type="file-done" /> {{ cg.orderCount || 0 }} 单</span>
                <span class="cg-stat cg-stat-rating">
                  <a-icon
                    v-for="n in 5"
                    :key="n"
                    type="star"
                    theme="filled"
                    :class="n <= Math.round(Number(cg.rating) || 5) ? 'star-on' : 'star-off'"
                  />
                  {{ cg.rating || "5.0" }}
                </span>
              </div>
              <p class="cg-meta">手机号：{{ cg.phone || "-" }}</p>
              <p class="cg-meta">身份证：{{ cg.idNo || "-" }}</p>
              <p class="cg-meta">KYC：{{ cg.kycStatusLabel || "-" }}</p>
              <a-tag :color="cg.status === 'idle' ? 'green' : 'orange'">
                {{ cg.statusLabel || (cg.status === "idle" ? "空闲" : "有任务") }}
              </a-tag>
              <div class="doc-tags">
                <a-tag color="blue">身份证 {{ cg.idCardPhotoCount || 0 }} 张</a-tag>
                <a-tag color="purple">资格证 {{ cg.certPhotoCount || 0 }} 张</a-tag>
              </div>
            </div>
            <div class="cg-actions">
              <a-button size="small" @click="preview(cg)">查看</a-button>
              <a-button size="small" @click="openEdit(cg)">编辑</a-button>
              <a-button size="small" type="danger" @click="handleDelete(cg)">删除</a-button>
            </div>
          </div>
        </div>
        <div v-else class="empty-wrap">
          <a-empty description="暂无护工档案">
            <a-button type="primary" @click="openCreate">新增护工</a-button>
          </a-empty>
        </div>
      </a-spin>
    </div>

    <div class="archive-pagination">
      <a-pagination
        :current="pagination.current"
        :pageSize="pagination.pageSize"
        :total="pagination.total"
        :showSizeChanger="true"
        :pageSizeOptions="pagination.pageSizeOptions"
        :showTotal="pagination.showTotal"
        @change="onPageChange"
        @showSizeChange="onPageSizeChange"
      />
    </div>

    <!-- 新增/编辑 -->
    <a-modal
      :visible="formVisible"
      :title="formMode === 'create' ? '新增护工档案' : '编辑护工档案'"
      :width="1120"
      :confirmLoading="formLoading"
      :bodyStyle="{ padding: '20px 24px' }"
      wrapClassName="caregiver-editor-modal"
      @ok="submitForm"
      @cancel="formVisible = false"
    >
      <a-form-model :model="form" :rules="rules" ref="formRef" layout="vertical">
        <div class="editor-layout">
          <!-- 左栏：基础与证件 -->
          <section class="editor-panel editor-panel--basic">
            <h4 class="editor-panel-title">基础与证件信息</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <a-form-model-item label="工号" prop="jobNo">
                <a-input v-model="form.jobNo" placeholder="如：HG2026001" />
              </a-form-model-item>
              <a-form-model-item label="姓名" prop="name">
                <a-input v-model="form.name" placeholder="请输入护工姓名" />
              </a-form-model-item>
              <a-form-model-item label="手机号" prop="mobile">
                <a-input v-model="form.mobile" placeholder="请输入手机号" />
              </a-form-model-item>
              <a-form-model-item label="身份证号" prop="idCard">
                <a-input v-model="form.idCard" placeholder="请输入身份证号" />
              </a-form-model-item>
              <a-form-model-item label="等级" prop="jobTitle">
                <a-select v-model="form.jobTitle">
                  <a-select-option value="金牌">金牌</a-select-option>
                  <a-select-option value="银牌">银牌</a-select-option>
                  <a-select-option value="专业">专业</a-select-option>
                </a-select>
              </a-form-model-item>
              <a-form-model-item label="性别">
                <a-select v-model="form.gender">
                  <a-select-option :value="1">男</a-select-option>
                  <a-select-option :value="0">女</a-select-option>
                </a-select>
              </a-form-model-item>
            </div>

            <a-form-model-item label="护工介绍" class="mb-0">
              <a-textarea
                v-model="form.intro"
                :rows="3"
                :maxLength="500"
                placeholder="展示在家属端护工详情/明星护工卡片，留空则按等级与评价自动生成"
              />
            </a-form-model-item>
            <a-form-model-item label="展示标签" class="mb-3">
              <a-select
                v-model="form.tags"
                mode="tags"
                :token-separators="[',', '，']"
                placeholder="输入后回车添加；留空则根据评价热词或等级默认展示"
                style="width: 100%"
              >
                <a-select-option v-for="tag in suggestedTags" :key="'s-' + tag" :value="tag">{{ tag }}</a-select-option>
              </a-select>
              <p v-if="suggestedTags.length" class="field-hint">
                评价热词参考：{{ suggestedTags.join("、") }}
              </p>
            </a-form-model-item>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div class="rounded-xl border border-slate-200 bg-white p-3">
                <p class="text-xs text-slate-500 mb-2">护工照片</p>
                <a-upload :beforeUpload="(f) => uploadImage(f, 'avatar')" :showUploadList="false" accept="image/*">
                  <a-button icon="upload" :loading="uploading.avatar" size="small">上传照片</a-button>
                </a-upload>
                <img v-if="form.avatar" :src="resolveUrl(form.avatar)" class="preview-img" alt="" />
              </div>
              <div class="rounded-xl border border-slate-200 bg-white p-3">
                <p class="text-xs text-slate-500 mb-2">身份证正面</p>
                <a-upload :beforeUpload="(f) => uploadCert(f, 'id_card_front')" :showUploadList="false" accept="image/*">
                  <a-button icon="upload" :loading="uploading.id_card_front" size="small">上传正面</a-button>
                </a-upload>
                <img v-if="certUrl('id_card_front')" :src="resolveUrl(certUrl('id_card_front'))" class="preview-img" alt="" />
              </div>
              <div class="rounded-xl border border-slate-200 bg-white p-3">
                <p class="text-xs text-slate-500 mb-2">身份证反面</p>
                <a-upload :beforeUpload="(f) => uploadCert(f, 'id_card_back')" :showUploadList="false" accept="image/*">
                  <a-button icon="upload" :loading="uploading.id_card_back" size="small">上传反面</a-button>
                </a-upload>
                <img v-if="certUrl('id_card_back')" :src="resolveUrl(certUrl('id_card_back'))" class="preview-img" alt="" />
              </div>
              <div class="rounded-xl border border-slate-200 bg-white p-3">
                <p class="text-xs text-slate-500 mb-2">资格证照片</p>
                <a-upload :beforeUpload="uploadHealthCert" :showUploadList="false" accept="image/*">
                  <a-button icon="upload" :loading="uploading.health_cert" size="small">上传资格证</a-button>
                </a-upload>
                <div class="preview-row">
                  <img
                    v-for="(url, idx) in healthCertUrls"
                    :key="'hc' + idx"
                    :src="resolveUrl(url)"
                    class="preview-img"
                    alt=""
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- 右栏：区域与每周出勤 -->
          <section class="editor-panel editor-panel--schedule">
            <h4 class="editor-panel-title">区域分配与每周出勤</h4>

            <div class="editor-field">
              <p class="editor-field-label">所属医院区域</p>
              <a-select
                v-model="form.hospitalAreas"
                mode="multiple"
                :placeholder="areaOptions.length ? '请选择医院区域' : '暂无医院，请先在「医院配置」中添加'"
                :disabled="!areaOptions.length"
                notFoundContent="暂无医院数据"
                style="width: 100%"
              >
                <a-select-option v-for="area in areaOptions" :key="area" :value="area">
                  {{ area }}
                </a-select-option>
              </a-select>
            </div>

            <div class="editor-field editor-field--grow">
              <p class="editor-field-label">每周固定出勤（建议 6 天）</p>
              <div class="weekly-schedule">
                <div
                  v-for="item in weeklySchedule"
                  :key="item.key"
                  class="weekly-day-row"
                  :class="{ 'weekly-day-row--disabled': !item.enabled }"
                >
                  <label class="weekly-day-label">
                    <a-checkbox v-model="item.enabled">{{ item.label }}</a-checkbox>
                  </label>
                  <div class="weekly-day-times">
                    <input
                      v-model="item.start"
                      :disabled="!item.enabled"
                      type="time"
                      class="weekly-time-input"
                    />
                    <span class="weekly-time-sep">至</span>
                    <input
                      v-model="item.end"
                      :disabled="!item.enabled"
                      type="time"
                      class="weekly-time-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section class="editor-calendar-section">
          <p class="editor-field-label">30天排班防撞单动态日历</p>
          <div class="calendar-panel">
            <div class="calendar-week-head">
              <span v-for="w in weekHead" :key="w">{{ w }}</span>
            </div>
            <div class="calendar-grid">
              <div
                v-for="cell in calendarCells"
                :key="cell.dateKey"
                class="calendar-cell"
                :class="calendarCellClass(cell)"
                @click="toggleDate(cell)"
              >
                <p class="calendar-cell-date">{{ cell.label }}</p>
                <p class="calendar-cell-week">{{ cell.weekLabel }}</p>
                <div v-if="cell.status === 'booked'" class="calendar-cell-booked">
                  已订：{{ cell.bookingText }}
                </div>
                <div v-else-if="cell.selected" class="calendar-cell-selected">已选</div>
              </div>
            </div>
          </div>
        </section>
      </a-form-model>
    </a-modal>

    <!-- 详情 -->
    <a-modal
      :visible="detailVisible"
      :footer="null"
      :width="720"
      wrapClassName="caregiver-detail-modal"
      @cancel="detailVisible = false"
    >
      <template slot="title">
        <span>护工档案详情</span>
      </template>
      <div v-if="detail" class="detail-shell">
        <div class="detail-hero">
          <div class="detail-avatar-wrap">
            <img
              v-if="hasCaregiverAvatar(detail)"
              :src="resolveUrl(detail.avatar)"
              class="detail-avatar-img"
              alt=""
              @error="markAvatarFailed(detail.id)"
            />
            <img v-else :src="defaultCaregiverAvatar" class="detail-avatar-img detail-avatar-img--default" alt="" />
          </div>
          <div class="detail-hero-body">
            <div class="detail-name-row">
              <h3>{{ detail.name }}</h3>
              <span class="level-tag" :class="'level-' + detail.levelColor">{{ detail.level }}</span>
              <a-tag :color="detail.status === 'idle' ? 'green' : 'orange'">
                {{ detail.statusLabel || (detail.status === "idle" ? "空闲" : "有任务") }}
              </a-tag>
            </div>
            <p class="detail-phone">{{ detail.phone || "-" }}</p>
            <div class="detail-stats-bar">
              <div class="detail-stat-item">
                <p class="stat-num">{{ detail.orderCount || 0 }}</p>
                <p class="stat-lbl">累计订单</p>
              </div>
              <div class="detail-stat-item">
                <p class="stat-num">{{ detail.reviewCount || 0 }}</p>
                <p class="stat-lbl">评价数</p>
              </div>
              <div class="detail-stat-item">
                <p class="stat-num">{{ detail.positiveRate || "100" }}%</p>
                <p class="stat-lbl">好评率</p>
              </div>
              <div class="detail-stat-item rating-item">
                <div class="rating-stars">
                  <a-icon
                    v-for="n in 5"
                    :key="n"
                    type="star"
                    theme="filled"
                    :class="n <= Math.round(Number(detail.rating) || 5) ? 'star-on' : 'star-off'"
                  />
                </div>
                <p class="stat-num rating-num">{{ detail.rating || "5.0" }}</p>
                <p class="stat-lbl">综合评分</p>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-info-grid">
          <div class="detail-info-item">
            <span class="info-k">工号</span>
            <span class="info-v">{{ detail.jobNo || "-" }}</span>
          </div>
          <div class="detail-info-item">
            <span class="info-k">身份证号</span>
            <span class="info-v">{{ detail.idCardFull || detail.idNo || "-" }}</span>
          </div>
          <div class="detail-info-item">
            <span class="info-k">服务时长</span>
            <span class="info-v">{{ formatServiceDuration(detail) }}</span>
          </div>
          <div class="detail-info-item">
            <span class="info-k">KYC 状态</span>
            <span class="info-v">{{ detail.kycStatusLabel || "-" }}</span>
          </div>
          <div class="detail-info-item" v-if="detail.hospitalAreas && detail.hospitalAreas.length">
            <span class="info-k">服务区域</span>
            <span class="info-v">{{ detail.hospitalAreas.join("、") }}</span>
          </div>
        </div>

        <div v-if="detail.intro" class="detail-intro-block">
          <h4>护工介绍</h4>
          <p>{{ detail.intro }}</p>
        </div>
        <div v-if="detail.tags && detail.tags.length" class="detail-tags-block">
          <h4>展示标签</h4>
          <div class="detail-tag-list">
            <a-tag v-for="tag in detail.tags" :key="tag" color="blue">{{ tag }}</a-tag>
          </div>
        </div>

        <div v-if="detail.photos && detail.photos.length" class="detail-gallery">
          <h4>护工照片</h4>
          <div class="gallery-row">
            <img
              v-for="(img, i) in detail.photos"
              :key="'p' + i"
              :src="resolveUrl(img)"
              class="gallery-img"
              alt=""
              @click="previewImage(resolveUrl(img))"
            />
          </div>
        </div>
        <div v-if="detail.idCardPhotos && detail.idCardPhotos.length" class="detail-gallery">
          <h4>身份证照片</h4>
          <div class="gallery-row">
            <img
              v-for="(img, i) in detail.idCardPhotos"
              :key="'id' + i"
              :src="resolveUrl(img)"
              class="gallery-img"
              alt=""
              @click="previewImage(resolveUrl(img))"
            />
          </div>
        </div>
        <div v-if="detail.certPhotos && detail.certPhotos.length" class="detail-gallery">
          <h4>资格证照片</h4>
          <div class="gallery-row">
            <img
              v-for="(img, i) in detail.certPhotos"
              :key="'c' + i"
              :src="resolveUrl(img)"
              class="gallery-img"
              alt=""
              @click="previewImage(resolveUrl(img))"
            />
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { dispatchApi } from "./dispatchApi";

const defaultCaregiverAvatar = require("../../assets/caregiver-avatar-default.svg");

export default {
  name: "DispatchCaregivers",
  data() {
    return {
      defaultCaregiverAvatar,
      avatarFailedIds: {},
      loading: false,
      keyword: "",
      caregivers: [],
      detailVisible: false,
      detail: null,
      bodyStyle: {},
      formVisible: false,
      formLoading: false,
      formMode: "create",
      editingId: null,
      form: this.getEmptyForm(),
      areaOptions: [],
      weeklySchedule: [],
      bookedDateMap: {},
      warnDateKey: "",
      uploading: {
        avatar: false,
        id_card_front: false,
        id_card_back: false,
        health_cert: false,
      },
      suggestedTags: [],
      rules: {
        jobNo: [{ required: true, message: "请输入工号", trigger: "blur" }],
        name: [{ required: true, message: "请输入姓名", trigger: "blur" }],
        mobile: [
          { required: true, message: "请输入手机号", trigger: "blur" },
          { pattern: /^\d{11}$/, message: "手机号须为11位数字", trigger: "blur" },
        ],
        idCard: [
          { required: true, message: "请输入身份证号", trigger: "blur" },
          { pattern: /^[0-9]{17}[0-9Xx]$/, message: "身份证号须为18位", trigger: "blur" },
        ],
        jobTitle: [{ required: true, message: "请选择等级", trigger: "change" }],
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["10", "30", "50", "100"],
        showTotal: (total) => `共 ${total} 条`,
      },
      weekHead: ["周一", "周二", "周三", "周四", "周五", "周六", "周日"],
    };
  },
  computed: {
    healthCertUrls() {
      return (this.form.certificates || [])
        .filter((c) => c.certType === "health_cert")
        .map((c) => c.fileUrl);
    },
    calendarCells() {
      const cells = [];
      const now = new Date();
      for (let i = 0; i < 30; i += 1) {
        const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
        const dateKey = this.formatDateKey(date);
        const weekIdx = date.getDay();
        const schedule = this.weeklySchedule[weekIdx];
        const disabledByWeek = !(schedule && schedule.enabled);
        const booking = this.bookedDateMap[dateKey];
        let status = "free";
        if (booking) status = "booked";
        else if (disabledByWeek) status = "vacation";
        cells.push({
          dateKey,
          label: `${date.getMonth() + 1}/${date.getDate()}`,
          weekLabel: this.weekLabel(weekIdx),
          selected: (this.form.selectedDates || []).includes(dateKey),
          status,
          bookingText: booking || "",
        });
      }
      return cells;
    },
  },
  mounted() {
    this.loadHospitalAreas();
    this.loadCaregivers();
    this.updateBodyHeight();
    window.addEventListener("resize", this.updateBodyHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateBodyHeight);
  },
  methods: {
    async loadHospitalAreas() {
      try {
        const data = await dispatchApi.pageHospitals({ current: 1, size: 200 });
        const options = (data.records || [])
          .filter((item) => Number(item.state) !== 1)
          .map((item) => {
            const name = item.hospitalName || "";
            const branch = item.branchType || "";
            return branch ? `${name}${branch}` : name;
          })
          .filter(Boolean);
        this.areaOptions = Array.from(new Set(options));
      } catch (e) {
        this.areaOptions = [];
      }
    },
    getEmptyForm() {
      return {
        jobNo: "",
        name: "",
        mobile: "",
        idCard: "",
        jobTitle: "专业",
        gender: 1,
        avatar: "",
        intro: "",
        tags: [],
        certificates: [],
        hospitalAreas: [],
        selectedDates: [],
      };
    },
    buildDefaultWeeklySchedule() {
      return [
        { key: "sun", label: "周日", enabled: false, start: "08:00", end: "18:00" },
        { key: "mon", label: "周一", enabled: true, start: "08:00", end: "18:00" },
        { key: "tue", label: "周二", enabled: true, start: "08:00", end: "18:00" },
        { key: "wed", label: "周三", enabled: true, start: "08:00", end: "18:00" },
        { key: "thu", label: "周四", enabled: true, start: "08:00", end: "18:00" },
        { key: "fri", label: "周五", enabled: true, start: "08:00", end: "18:00" },
        { key: "sat", label: "周六", enabled: true, start: "08:00", end: "18:00" },
      ];
    },
    weekLabel(i) {
      return ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][i];
    },
    formatDateKey(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    calendarCellClass(cell) {
      if (cell.status === "vacation") {
        return "bg-striped-slate border-slate-200 text-slate-400 pointer-events-none";
      }
      if (cell.status === "booked") {
        const warn = this.warnDateKey === cell.dateKey ? "ring-2 ring-red-500" : "";
        return `bg-white border-slate-200 ${warn}`;
      }
      const selected = cell.selected ? "ring-2 ring-blue-500 bg-blue-50" : "bg-white hover:bg-blue-50";
      return `border-slate-200 cursor-pointer ${selected}`;
    },
    toggleDate(cell) {
      if (cell.status === "vacation") return;
      if (cell.status === "booked") {
        this.warnDateKey = cell.dateKey;
        setTimeout(() => {
          if (this.warnDateKey === cell.dateKey) this.warnDateKey = "";
        }, 800);
        this.$message.warning("该日期已被订单占用，无法勾选");
        return;
      }
      const selected = new Set(this.form.selectedDates || []);
      if (selected.has(cell.dateKey)) selected.delete(cell.dateKey);
      else selected.add(cell.dateKey);
      this.form.selectedDates = Array.from(selected);
    },
    async loadBookedDates(caregiverId) {
      if (!caregiverId) {
        this.bookedDateMap = {};
        return;
      }
      try {
        const data = await dispatchApi.pageOrders({ current: 1, size: 100, status: "all" });
        const map = {};
        (data.records || []).forEach((row) => {
          if (row.caregiverId !== caregiverId) return;
          if (!row.startDate || !row.endDate) return;
          const start = new Date(`${row.startDate}T00:00:00`);
          const end = new Date(`${row.endDate}T00:00:00`);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
          let cursor = new Date(start);
          while (cursor <= end) {
            const key = this.formatDateKey(cursor);
            map[key] = `${row.orderNo} (${row.patientName || "用户"})`;
            cursor.setDate(cursor.getDate() + 1);
          }
        });
        this.bookedDateMap = map;
      } catch (e) {
        this.bookedDateMap = {};
      }
    },
    resolveUrl(url) {
      if (!url) return "";
      if (url.startsWith("http") || url.startsWith("data:")) return url;
      return url;
    },
    hasCaregiverAvatar(cg) {
      if (!cg || !cg.avatar || this.avatarFailedIds[cg.id]) return false;
      const url = String(cg.avatar);
      return url.includes("/caregivers/files/") || url.includes("/upload/");
    },
    markAvatarFailed(id) {
      if (id == null) return;
      this.$set(this.avatarFailedIds, id, true);
    },
    formatServiceDuration(detail) {
      if (!detail) return "0 小时";
      const hours = Number(detail.hours) || 0;
      const days = Number(detail.serviceDays) || (hours > 0 ? Math.round(hours / 8) : 0);
      const orders = Number(detail.orderCount) || 0;
      if (orders <= 0 && hours <= 0) {
        return "暂无完工记录";
      }
      return `${hours} 小时（${days} 天 · ${orders} 单完工）`;
    },
    certUrl(type) {
      const item = (this.form.certificates || []).find((c) => c.certType === type);
      return item ? item.fileUrl : "";
    },
    setCertUrl(type, url) {
      const list = [...(this.form.certificates || [])];
      const idx = list.findIndex((c) => c.certType === type);
      if (idx >= 0) {
        list[idx] = { certType: type, fileUrl: url };
      } else {
        list.push({ certType: type, fileUrl: url });
      }
      this.form.certificates = list;
    },
    previewImage(url) {
      if (!url) return;
      window.open(url, "_blank");
    },
    async loadCaregivers() {
      this.loading = true;
      this.avatarFailedIds = {};
      try {
        const data = await dispatchApi.pageCaregivers({
          keyword: this.keyword.trim(),
          current: this.pagination.current,
          size: this.pagination.pageSize,
        });
        this.caregivers = data.records || [];
        this.pagination.total = data.total || 0;
        this.pagination.current = data.current || this.pagination.current;
        this.pagination.pageSize = data.size || this.pagination.pageSize;
      } catch (e) {
        this.$message.error(e.message || "护工档案加载失败");
      } finally {
        this.loading = false;
        this.$nextTick(this.updateBodyHeight);
      }
    },
    handleSearch() {
      this.pagination.current = 1;
      this.loadCaregivers();
    },
    onPageChange(page, pageSize) {
      this.pagination.current = page;
      this.pagination.pageSize = pageSize;
      this.loadCaregivers();
    },
    onPageSizeChange(current, size) {
      this.pagination.current = 1;
      this.pagination.pageSize = size;
      this.loadCaregivers();
    },
    openCreate() {
      this.formMode = "create";
      this.editingId = null;
      this.form = this.getEmptyForm();
      this.suggestedTags = [];
      this.weeklySchedule = this.buildDefaultWeeklySchedule();
      this.bookedDateMap = {};
      this.formVisible = true;
    },
    async openEdit(cg) {
      try {
        const detail = await dispatchApi.caregiverDetail(cg.id);
        this.formMode = "edit";
        this.editingId = cg.id;
        this.form = {
          jobNo: detail.jobNo || "",
          name: detail.name,
          mobile: detail.phone,
          idCard: detail.idCardFull || detail.idNo || "",
          jobTitle: detail.level || "专业",
          gender: 1,
          avatar: detail.avatar || "",
          intro: detail.intro || "",
          tags: detail.tags ? [...detail.tags] : [],
          certificates: detail.certificates ? [...detail.certificates] : [],
          hospitalAreas: detail.hospitalAreas ? [...detail.hospitalAreas] : [],
          selectedDates: detail.selectedDates ? [...detail.selectedDates] : [],
        };
        this.weeklySchedule =
          detail.weeklySchedule && detail.weeklySchedule.length
            ? detail.weeklySchedule.map((x) => ({ ...x }))
            : this.buildDefaultWeeklySchedule();
        this.suggestedTags = detail.suggestedTags || [];
        await this.loadBookedDates(cg.id);
        this.formVisible = true;
      } catch (e) {
        this.$message.error(e.message || "加载护工信息失败");
      }
    },
    async preview(cg) {
      try {
        this.detail = await dispatchApi.caregiverDetail(cg.id);
        this.detailVisible = true;
      } catch (e) {
        this.$message.error(e.message || "加载详情失败");
      }
    },
    handleDelete(cg) {
      this.$confirm({
        title: "确认删除",
        content: `确定删除护工「${cg.name}」吗？`,
        onOk: async () => {
          try {
            await dispatchApi.deleteCaregiver(cg.id);
            this.$message.success("删除成功");
            this.loadCaregivers();
          } catch (e) {
            this.$message.error(e.message || "删除失败");
          }
        },
      });
    },
    async uploadImage(file, field) {
      this.uploading[field] = true;
      try {
        const data = await dispatchApi.uploadCaregiverFile(file);
        this.form[field] = data.url;
        this.$message.success("上传成功");
      } catch (e) {
        this.$message.error(e.message || "上传失败");
      } finally {
        this.uploading[field] = false;
      }
      return false;
    },
    async uploadCert(file, type) {
      this.uploading[type] = true;
      try {
        const data = await dispatchApi.uploadCaregiverFile(file);
        this.setCertUrl(type, data.url);
        this.$message.success("上传成功");
      } catch (e) {
        this.$message.error(e.message || "上传失败");
      } finally {
        this.uploading[type] = false;
      }
      return false;
    },
    async uploadHealthCert(file) {
      this.uploading.health_cert = true;
      try {
        const data = await dispatchApi.uploadCaregiverFile(file);
        this.form.certificates = [...(this.form.certificates || []), { certType: "health_cert", fileUrl: data.url }];
        this.$message.success("上传成功");
      } catch (e) {
        this.$message.error(e.message || "上传失败");
      } finally {
        this.uploading.health_cert = false;
      }
      return false;
    },
    submitForm() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        const payload = {
          jobNo: this.form.jobNo,
          name: this.form.name,
          mobile: this.form.mobile,
          idCard: String(this.form.idCard || "").toUpperCase(),
          jobTitle: this.form.jobTitle,
          gender: this.form.gender,
          avatar: this.form.avatar,
          intro: this.form.intro || "",
          tags: this.form.tags || [],
          certificates: this.form.certificates || [],
          hospitalAreas: this.form.hospitalAreas || [],
          weeklySchedule: this.weeklySchedule || [],
          selectedDates: this.form.selectedDates || [],
        };
        try {
          if (this.formMode === "create") {
            await dispatchApi.createCaregiver(payload);
            this.$message.success("新增成功");
          } else {
            await dispatchApi.updateCaregiver(this.editingId, payload);
            this.$message.success("更新成功");
          }
          this.formVisible = false;
          this.loadCaregivers();
        } catch (e) {
          this.$message.error(e.message || "保存失败");
        } finally {
          this.formLoading = false;
        }
      });
    },
    updateBodyHeight() {
      this.$nextTick(() => {
        const body = this.$refs.archiveBody;
        if (!body) return;
        const rect = body.getBoundingClientRect();
        const paginationH = 56;
        const bottomGap = 20;
        const height = Math.max(280, window.innerHeight - rect.top - paginationH - bottomGap);
        this.bodyStyle = { height: `${height}px` };
      });
    },
  },
};
</script>

<style scoped>
.caregiver-archive {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  box-sizing: border-box;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.page-header h2 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.search-input {
  width: 220px;
}

.cg-stats-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.375rem 0;
  flex-wrap: wrap;
}
.cg-stat {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #475569;
  font-weight: 500;
}
.cg-stat-rating .star-on {
  color: #f59e0b;
  font-size: 11px;
}
.cg-stat-rating .star-off {
  color: #e2e8f0;
  font-size: 11px;
}
.detail-shell {
  padding-top: 0.25rem;
}
.detail-hero {
  display: flex;
  gap: 1.25rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  margin-bottom: 1rem;
}
.detail-avatar-wrap {
  flex-shrink: 0;
}
.detail-avatar-img,
.detail-avatar-fallback {
  width: 88px;
  height: 88px;
  border-radius: 1rem;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.detail-avatar-img--default {
  object-fit: contain;
  background: #eff6ff;
  padding: 8px;
}

.detail-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
}
.detail-hero-body {
  flex: 1;
  min-width: 0;
}
.detail-name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}
.detail-name-row h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}
.detail-phone {
  margin: 0 0 0.875rem;
  font-size: 0.875rem;
  color: #64748b;
}
.detail-stats-bar {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}
.detail-stat-item {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.625rem 0.75rem;
  text-align: center;
}
.stat-num {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #0082d4;
  line-height: 1.2;
}
.stat-lbl {
  margin: 0.25rem 0 0;
  font-size: 0.6875rem;
  color: #94a3b8;
}
.rating-item >>> .ant-rate {
  font-size: 14px;
}
.rating-stars {
  display: flex;
  justify-content: center;
  gap: 2px;
  margin-bottom: 0.25rem;
}
.rating-stars .star-on {
  color: #f59e0b;
  font-size: 14px;
}
.rating-stars .star-off {
  color: #e2e8f0;
  font-size: 14px;
}
.rating-num {
  font-size: 1.125rem !important;
}
.detail-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.detail-info-item {
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 0.625rem;
  border: 1px solid #f1f5f9;
}
.info-k {
  display: block;
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-bottom: 0.25rem;
}
.info-v {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  word-break: break-all;
}
.detail-gallery h4 {
  margin: 0 0 0.625rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
}
.detail-gallery {
  margin-bottom: 1rem;
}
.gallery-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}
.gallery-img {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 0.625rem;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}
.gallery-img:hover {
  transform: scale(1.03);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.archive-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem;
}

.empty-wrap {
  height: 100%;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.caregiver-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1rem;
}

.cg-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.cg-avatar-wrap {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
}

.cg-photo {
  width: 64px;
  height: 64px;
  border-radius: 0.625rem;
  object-fit: cover;
  border: 1px solid #e2e8f0;
}

.cg-photo--default {
  object-fit: contain;
  background: #eff6ff;
  padding: 4px;
}

.cg-info {
  flex: 1;
  min-width: 0;
}

.cg-actions {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex-shrink: 0;
}

.cg-name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cg-name {
  font-weight: 600;
  color: #0f172a;
}

.level-tag {
  font-size: 0.6875rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-weight: 600;
}

.level-gold { background: #fef3c7; color: #b45309; }
.level-pro { background: #dbeafe; color: #1d4ed8; }
.level-silver { background: #f1f5f9; color: #64748b; }

.cg-meta {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}

.doc-tags {
  margin-top: 0.5rem;
}
.field-hint {
  margin: 0.375rem 0 0;
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.4;
}
.detail-intro-block,
.detail-tags-block {
  margin-bottom: 1rem;
}
.detail-intro-block h4,
.detail-tags-block h4 {
  margin: 0 0 0.5rem;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.detail-intro-block p {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}
.detail-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.archive-pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding-top: 0.75rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 0.875rem;
}

.preview-row {
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.preview-img,
.detail-img {
  width: 88px;
  height: 88px;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid #e2e8f0;
  margin-top: 0.5rem;
  margin-right: 0.5rem;
}

.detail-photos {
  margin-top: 0.75rem;
}

.bg-striped-slate {
  background-image: repeating-linear-gradient(
    -45deg,
    #f1f5f9,
    #f1f5f9 8px,
    #e2e8f0 8px,
    #e2e8f0 16px
  );
}

.caregiver-editor-modal >>> .ant-modal {
  width: 1120px !important;
  max-width: calc(100vw - 48px) !important;
  top: 32px;
}

.caregiver-editor-modal >>> .ant-modal-body {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

.editor-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;
}

.editor-panel {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 18px;
  min-width: 0;
}

.editor-panel--basic {
  background: #f8fafc;
}

.editor-panel--schedule {
  background: #fff;
  display: flex;
  flex-direction: column;
}

.editor-panel-title {
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

.editor-field {
  margin-bottom: 16px;
}

.editor-field--grow {
  flex: 1;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
}

.editor-field-label {
  margin: 0 0 8px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.weekly-schedule {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.weekly-day-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.weekly-day-row--disabled {
  opacity: 0.72;
}

.weekly-day-label {
  flex: 0 0 88px;
  margin: 0;
  white-space: nowrap;
}

.weekly-day-times {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.weekly-time-input {
  flex: 1;
  min-width: 108px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  color: #334155;
  background: #fff;
}

.weekly-time-input:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

.weekly-time-sep {
  flex-shrink: 0;
  font-size: 12px;
  color: #94a3b8;
}

.editor-calendar-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.calendar-panel {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #fff;
}

.calendar-week-head {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
}

.calendar-week-head span {
  text-align: center;
  font-size: 12px;
  color: #64748b;
  padding: 8px 0;
  font-weight: 600;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  padding: 12px;
  max-height: 280px;
  overflow-y: auto;
}

.calendar-cell {
  position: relative;
  min-height: 72px;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 12px;
  transition: border-color 0.15s, background-color 0.15s;
  cursor: pointer;
}

.calendar-cell-date {
  margin: 0;
  font-weight: 600;
  color: #0f172a;
}

.calendar-cell-week {
  margin: 2px 0 0;
  font-size: 10px;
  color: #94a3b8;
}

.calendar-cell-booked {
  margin-top: 6px;
  padding: 3px 6px;
  border-radius: 6px;
  background: #3b82f6;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-cell-selected {
  margin-top: 6px;
  font-size: 10px;
  color: #2563eb;
  font-weight: 600;
}

@media (max-width: 960px) {
  .editor-layout {
    grid-template-columns: 1fr;
  }

  .weekly-day-row {
    flex-wrap: wrap;
    gap: 10px;
  }

  .weekly-day-label {
    flex: 0 0 100%;
  }
}

@media (max-width: 720px) {
  .detail-stats-bar,
  .detail-info-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .calendar-week-head,
  .calendar-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}
</style>
